// useCachedList.ts — stale-while-revalidate para listas de catálogo.
//
// Round 2026-07-02 (perf /costos-sugeridos): la espera percibida al entrar a
// una página de catálogo era el viaje de red al backend + spinner sin datos.
// Este hook pinta AL INSTANTE la última lista conocida (sessionStorage) y
// revalida en segundo plano; el spinner solo aparece en la primera visita.
//
// Arquitectura copiada del único SWR real del proyecto (AuthContext: hidrata
// el user cacheado sincrónico al montar → revalida contra /auth/me → actualiza
// storage) y de las convenciones de cache de imageCompression.ts (envelope
// {at, data}, try/catch contra quota/Safari privado).
//
// Contrato del fetcher: como apiFetch NUNCA lanza (retorna null en error y ya
// mostró el toast), si el fetcher devuelve null NO pisamos estado ni cache —
// se queda lo último conocido.
//
// ⚠ Claves: SIEMPRE prefijadas con "cache:" (clearCachedLists() las barre en
// logout) y versionadas ("...:v1") — si cambia el shape de las filas, bumpear
// la versión para no hidratar shapes viejos. Este hook es para datos GLOBALES
// (catálogos admin); si se reutiliza para datos per-user (ej. /tareas-cliente)
// es OBLIGATORIO incluir el user.id en la clave.

import { useCallback, useEffect, useRef, useState } from "react";

type Envelope<T> = { at: number; data: T[] };

const CACHE_PREFIX = "cache:";

function readCache<T>(key: string): T[] | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Envelope<T>;
    if (!parsed || !Array.isArray(parsed.data)) return null;
    return parsed.data;
  } catch {
    // Cache corrupto / storage bloqueado → como si no existiera.
    return null;
  }
}

/**
 * Borra TODOS los caches de listas (claves "cache:*") de sessionStorage.
 * Llamado desde AuthContext en logout (propio y cross-tab) como defensa en
 * profundidad — hoy los caches son catálogos globales admin-only, pero así
 * ningún dato queda colgado entre sesiones.
 */
export function clearCachedLists(): void {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(CACHE_PREFIX)) sessionStorage.removeItem(k);
    }
  } catch {
    // storage inaccesible → nada que limpiar
  }
}

/**
 * Lista con cache stale-while-revalidate.
 *
 * @param key   Clave de sessionStorage. Prefijar con "cache:" y versionar
 *              (ej. "cache:costos-sugeridos:v1").
 * @param fetcher Trae la lista fresca; `null` = error (no se pisa el estado).
 *              Puede ser una arrow inline — se lee vía ref, no re-dispara.
 * @returns data (cacheada al instante, luego fresca), loading (true SOLO en
 *          primera visita sin cache), refreshing (revalidación en curso),
 *          refresh() (drop-in del viejo fetchList: las mutaciones lo llaman),
 *          setData (para updates optimistas, ej. reorden — también cachea).
 */
export function useCachedList<T>(
  key: string,
  fetcher: () => Promise<T[] | null>,
): {
  data: T[];
  loading: boolean;
  refreshing: boolean;
  refresh: () => Promise<void>;
  setData: React.Dispatch<React.SetStateAction<T[]>>;
} {
  // Hidratación sincrónica: una sola lectura del cache en el primer render.
  const [initial] = useState<T[] | null>(() => readCache<T>(key));
  const [data, setData] = useState<T[]>(initial ?? []);
  const [loading, setLoading] = useState<boolean>(initial == null);
  const [refreshing, setRefreshing] = useState(false);

  // El fetcher vive en un ref para aceptar arrows inline sin re-disparar
  // el efecto de montaje ni invalidar la identidad de refresh().
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Persiste en cache cada vez que hay datos confirmados (tras revalidar o
  // por setData optimista). Mientras loading=true aún no hay datos reales:
  // no escribimos, para que una primera visita fallida no cachee [] y
  // convierta el spinner de la próxima visita en una tabla vacía.
  useEffect(() => {
    if (loading) return;
    try {
      const envelope: Envelope<T> = { at: Date.now(), data };
      sessionStorage.setItem(key, JSON.stringify(envelope));
    } catch {
      // QuotaExceeded / Safari privado → seguimos sin cache, sin romper.
    }
  }, [key, data, loading]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const fresh = await fetcherRef.current();
      if (fresh != null) setData(fresh);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Revalidar al montar (y si cambiara la key). El cache ya está pintado.
  useEffect(() => {
    void refresh();
  }, [refresh, key]);

  return { data, loading, refreshing, refresh, setData };
}
